import React, { useState, useEffect, Suspense, lazy } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
    Container,
    Row,
    Col,
    Card,
    Badge,
    Button,
    Spinner,
    Alert,
    Modal,
    Form,
} from 'react-bootstrap';

import SEO from '@/components/common/SEO';
import { useOrganizationApi } from '@/shared/services/organizationApi';
import { useAuth } from '@/shared/contexts/AuthContext';
import { useMembership } from '@/shared/contexts/MembershipContext';
import { useEcosystem } from '@/shared/contexts/EcosystemContext';
import useChatPageEffect from '@/shared/hooks/useChatPageEffect';
import useFullAuth from '@/features/auth/hooks/useFullAuth';
import OrganizationOpportunities from '@/features/opportunities/components/OrganizationOpportunities';
import { RILE_MARK_URL } from '@/shared/utils/brandAssets';

import './Organizations.css';
import AffiliatedPeople from './components/AffiliatedPeople';
import OrganizationNetworkDashboard from './components/OrganizationNetworkDashboard';

// Lazy load the subgraph component
const OrganizationSubgraph = lazy(
    () => import('@/features/graph/components/OrganizationSubgraph')
);

export default function OrganizationDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { ecosystem } = useEcosystem();
    const ecosystemName = ecosystem?.name || 'RILE Connect';
    const [organization, setOrganization] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState('');

    // Claim modal state
    const [showClaimModal, setShowClaimModal] = useState(false);
    const [claimData, setClaimData] = useState({
        roleAtOrg: '',
        additionalInfo: '',
    });
    const [submitting, setSubmitting] = useState(false);

    const orgApi = useOrganizationApi();
    const { user: firebaseUser } = useAuth();
    const { user: currentUser } = useFullAuth();
    const { role: membershipRole, isMember: isEcosystemMember } =
        useMembership();

    // Use Firebase user for "is logged in" check, full user for membership check
    const isLoggedIn = !!firebaseUser;

    useEffect(() => {
        const fetchOrganization = async () => {
            try {
                const data = await orgApi.fetchOrganization(id);
                setOrganization(data);
            } catch (err) {
                setError('Failed to load organization');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchOrganization();
    }, [id, orgApi]);

    // Publish page context for chat widget
    useChatPageEffect(
        {
            page: 'org-detail',
            organizationName: organization?.name,
            primarySector: organization?.primarySector,
            regionsServed: organization?.regionsServed,
            description: organization?.description?.substring(0, 200),
            connectionCount: organization?.connectionCount,
            offerings: organization?.offerings,
            needs: organization?.needs,
            programmingTypes: organization?.programmingTypes,
        },
        [organization]
    );

    // Check if user can edit this organization
    const isOrgEditor =
        currentUser &&
        organization?.managers?.some(
            (m) =>
                m.user === currentUser._id &&
                ['owner', 'admin'].includes(m.permissionRole)
        );
    // Ecosystem-admin override: `User.role === 'admin'` no longer exists
    // post-P4a (User.role is 'user' | 'platformAdmin'); admin authority for
    // a per-tenant resource lives on EcosystemMembership.role in the active
    // tenant (review finding 9). Variable kept as `isSystemAdmin` so the
    // prop name on downstream consumers (e.g. ManageManagers) does not churn.
    const isSystemAdmin = isEcosystemMember && membershipRole === 'admin';
    const canEdit = isOrgEditor || isSystemAdmin;

    // Check if organization is claimable
    const isClaimable =
        organization &&
        (!organization.managers ||
            organization.managers.length === 0 ||
            !organization.managers.some((m) => m.permissionRole === 'owner'));

    // Check if user already has a pending claim
    const hasPendingClaim =
        currentUser &&
        organization?.ownershipClaims?.some(
            (c) => c.user === currentUser._id && c.status === 'pending'
        );

    // Check whether the user already holds a management grant here
    const isManager =
        currentUser &&
        organization?.managers?.some((m) => m.user === currentUser._id);

    const handleClaimSubmit = async () => {
        try {
            setSubmitting(true);
            await orgApi.claimOwnership(id, claimData);
            setSuccess(
                'Your affiliation request has been submitted. An admin will review it shortly.'
            );
            setShowClaimModal(false);
            // Refresh organization data
            const data = await orgApi.fetchOrganization(id);
            setOrganization(data);
            setTimeout(() => setSuccess(''), 5000);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to submit claim');
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancelClaim = async () => {
        try {
            setSubmitting(true);
            await orgApi.cancelClaim(id);
            setSuccess('Affiliation request cancelled.');
            // Refresh organization data
            const data = await orgApi.fetchOrganization(id);
            setOrganization(data);
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to cancel claim');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <Container className="py-5 text-center">
                <Spinner animation="border" variant="primary" />
            </Container>
        );
    }

    if (error || !organization) {
        return (
            <Container className="py-5 text-center">
                <p className="text-danger">
                    {error || 'Organization not found'}
                </p>
                <Button as={Link} to="/directory" variant="primary">
                    Back to Ecosystem
                </Button>
            </Container>
        );
    }

    return (
        <>
            <SEO
                title={organization.name}
                description={
                    organization.description?.substring(0, 160) ||
                    `${organization.name}, a member of ${ecosystemName}.`
                }
                canonical={`/organizations/${id}`}
                breadcrumbs={[
                    { name: 'Home', path: '/' },
                    { name: 'Ecosystem', path: '/directory' },
                    {
                        name: organization.name,
                        path: `/organizations/${id}`,
                    },
                ]}
            />
            <Container className="py-5">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <Link to="/directory" className="text-primary">
                        ← Back to Ecosystem
                    </Link>
                    <div className="d-flex gap-2">
                        {canEdit && (
                            <Button
                                variant={
                                    isSystemAdmin && !isOrgEditor
                                        ? 'outline-info'
                                        : 'outline-primary'
                                }
                                className="rounded-pill"
                                onClick={() =>
                                    navigate(`/organizations/${id}/edit`)
                                }
                            >
                                Edit Organization
                                {isSystemAdmin && !isOrgEditor && (
                                    <Badge
                                        bg="info"
                                        className="ms-2 admin-edit-badge"
                                    >
                                        Admin
                                    </Badge>
                                )}
                            </Button>
                        )}
                        {isLoggedIn &&
                            isClaimable &&
                            !isManager &&
                            !hasPendingClaim && (
                                <Button
                                    variant="primary"
                                    className="rounded-pill"
                                    onClick={() => setShowClaimModal(true)}
                                >
                                    I Work Here
                                </Button>
                            )}
                        {hasPendingClaim && (
                            <Button
                                variant="outline-warning"
                                className="rounded-pill"
                                onClick={handleCancelClaim}
                                disabled={submitting}
                            >
                                {submitting
                                    ? 'Cancelling...'
                                    : 'Cancel Request'}
                            </Button>
                        )}
                    </div>
                </div>

                {error && (
                    <Alert
                        variant="danger"
                        dismissible
                        onClose={() => setError(null)}
                    >
                        {error}
                    </Alert>
                )}
                {success && <Alert variant="success">{success}</Alert>}

                <Row>
                    <Col lg={8}>
                        <Card className="border-0 shadow-sm mb-4">
                            <Card.Body className="p-4">
                                <div className="d-flex align-items-start mb-4">
                                    <img
                                        src={
                                            organization.imageUrl ||
                                            RILE_MARK_URL
                                        }
                                        alt={organization.name}
                                        className="org-detail-image me-4"
                                        onError={(e) => {
                                            e.target.src = RILE_MARK_URL;
                                        }}
                                    />
                                    <div>
                                        <h1 className="text-primary mb-2">
                                            {organization.name}
                                        </h1>
                                        <Badge bg="primary" className="me-2">
                                            {organization.primarySector}
                                        </Badge>
                                        {organization.secondarySectors?.map(
                                            (sector) => (
                                                <Badge
                                                    bg="secondary"
                                                    key={sector}
                                                    className="me-2"
                                                >
                                                    {sector}
                                                </Badge>
                                            )
                                        )}
                                        {organization.status === 'pending' && (
                                            <Badge
                                                bg="warning"
                                                className="ms-2"
                                            >
                                                Pending Approval
                                            </Badge>
                                        )}
                                    </div>
                                </div>

                                {organization.description && (
                                    <section className="mb-4">
                                        <h5 className="text-primary">About</h5>
                                        <p>{organization.description}</p>
                                    </section>
                                )}

                                {organization.regionsServed?.length > 0 && (
                                    <section className="mb-4">
                                        <h5 className="text-primary">
                                            Regions Served
                                        </h5>
                                        <div>
                                            {organization.regionsServed.map(
                                                (region) => (
                                                    <Badge
                                                        bg="light"
                                                        text="dark"
                                                        key={region}
                                                        className="me-2 mb-2"
                                                    >
                                                        {region}
                                                    </Badge>
                                                )
                                            )}
                                        </div>
                                    </section>
                                )}

                                {organization.programmingTypes?.length > 0 && (
                                    <section className="mb-4">
                                        <h5 className="text-primary">
                                            Programming Types
                                        </h5>
                                        <ul>
                                            {organization.programmingTypes.map(
                                                (program) => (
                                                    <li key={program}>
                                                        {program}
                                                    </li>
                                                )
                                            )}
                                        </ul>
                                    </section>
                                )}

                                {organization.offerings?.length > 0 && (
                                    <section className="mb-4">
                                        <h5 className="text-primary">
                                            What We Offer
                                        </h5>
                                        <ul>
                                            {organization.offerings.map(
                                                (item) => (
                                                    <li key={item}>{item}</li>
                                                )
                                            )}
                                        </ul>
                                    </section>
                                )}

                                {organization.needs?.length > 0 && (
                                    <section className="mb-4">
                                        <h5 className="text-primary">
                                            What We Need
                                        </h5>
                                        <ul>
                                            {organization.needs.map((item) => (
                                                <li key={item}>{item}</li>
                                            ))}
                                        </ul>
                                    </section>
                                )}
                            </Card.Body>
                        </Card>

                        {/* Organization Opportunities */}
                        <OrganizationOpportunities
                            organizationId={id}
                            organizationName={organization.name}
                            canEdit={canEdit}
                        />
                    </Col>

                    <Col lg={4}>
                        <Card className="border-0 shadow-sm mb-4">
                            <Card.Body className="p-4">
                                <h5 className="text-primary mb-4">Contact</h5>

                                {organization.email && (
                                    <p className="mb-3">
                                        <strong>Email:</strong>
                                        <br />
                                        <a
                                            href={`mailto:${organization.email}`}
                                        >
                                            {organization.email}
                                        </a>
                                    </p>
                                )}

                                {organization.website && (
                                    <p className="mb-3">
                                        <strong>Website:</strong>
                                        <br />
                                        <a
                                            href={organization.website}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {organization.website}
                                        </a>
                                    </p>
                                )}

                                <hr />

                                <Button
                                    variant="outline-primary"
                                    className="w-100 rounded-pill"
                                    disabled
                                >
                                    Find Related Mentors{' '}
                                    <small className="opacity-75">
                                        (Coming Soon)
                                    </small>
                                </Button>
                            </Card.Body>
                        </Card>

                        {/* Network Dashboard */}
                        <OrganizationNetworkDashboard
                            organizationId={id}
                            organizationName={organization.name}
                        />

                        {/* Network Connections Subgraph */}
                        <Suspense
                            fallback={
                                <Card className="border-0 shadow-sm">
                                    <Card.Body className="p-4 text-center">
                                        <Spinner
                                            animation="border"
                                            variant="primary"
                                            size="sm"
                                        />
                                        <p className="mt-2 mb-0 small text-muted">
                                            Loading connections...
                                        </p>
                                    </Card.Body>
                                </Card>
                            }
                        >
                            <OrganizationSubgraph
                                organizationId={id}
                                height={300}
                            />
                        </Suspense>

                        {/* Affiliated People Section */}
                        <AffiliatedPeople organizationId={id} />
                    </Col>
                </Row>

                {/* I Work Here Modal */}
                <Modal
                    show={showClaimModal}
                    onHide={() => setShowClaimModal(false)}
                >
                    <Modal.Header closeButton>
                        <Modal.Title>I Work Here</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>
                            You are requesting to confirm that you work at{' '}
                            <strong>{organization.name}</strong>. An
                            administrator will review your request.
                        </p>
                        <Form.Group className="mb-3">
                            <Form.Label>
                                Your Role at This Organization
                            </Form.Label>
                            <Form.Control
                                type="text"
                                value={claimData.roleAtOrg}
                                onChange={(e) =>
                                    setClaimData({
                                        ...claimData,
                                        roleAtOrg: e.target.value,
                                    })
                                }
                                placeholder="e.g., Executive Director, Program Manager"
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Additional Information</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={claimData.additionalInfo}
                                onChange={(e) =>
                                    setClaimData({
                                        ...claimData,
                                        additionalInfo: e.target.value,
                                    })
                                }
                                placeholder="Please provide any additional information that can help verify your connection to this organization..."
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            variant="secondary"
                            onClick={() => setShowClaimModal(false)}
                            disabled={submitting}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleClaimSubmit}
                            disabled={submitting}
                        >
                            {submitting ? (
                                <Spinner animation="border" size="sm" />
                            ) : (
                                'Submit Request'
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </Container>
        </>
    );
}
